import { actions as notifActions } from 'redux-notifications';
import { IStore } from 'redux/IStore';
import { Dispatch } from 'react-redux';
import * as i18n from 'i18next';
import { IPaymentMethodState, IUserPaymentMethod, IUserPaymentMethodsAction } from 'models/user/userpayments';

const { notifSend } = notifActions;

/** Action Types */
export const REQUEST_START: string = 'paymentMethods/REQUEST_START';
export const REQUEST_FAILURE: string = 'paymentMethods/REQUEST_FAILURE';
export const GET_PAYMENT_METHODS_SUCCESS: string = 'paymentMethods/GET_PAYMENT_METHODS_SUCCESS';
export const ADD_USER_PAYMENT_METHOD_SUCCESS: string = 'paymentMethods/ADD_USER_PAYMENT_METHOD_SUCCESS';

/** Initial State */
const getinitialState = (): IPaymentMethodState => ({
	items: null,
});

/** Reducer */
export function paymentMethodsReducer(
	state = getinitialState(),
	action: IUserPaymentMethodsAction,
): IPaymentMethodState {
	switch (action.type) {
		case REQUEST_START:
			return {
				...state,
				isFetching: true,
				error: false,
			};

		case REQUEST_FAILURE:
			return {
				...state,
				isFetching: false,
				message: action.payload.message,
				error: true,
			};

		case GET_PAYMENT_METHODS_SUCCESS:
			return {
				...state,
				isFetching: false,
				items: action.payload.items,
			};

		case ADD_USER_PAYMENT_METHOD_SUCCESS:
			return {
				...state,
				isFetching: false,
				items: [...state.items, action.payload.paymentMethod],
			};

		default:
			return state;
	}
}

export function getUserPaymentMethods() {
	return (dispatch: Dispatch<IStore>) => {
		dispatch(requestStart());
		//TODO: remove this dispatch when BE is done
		dispatch(
			getPaymentMethodsSuccess([
				{
					id: '1',
					cardNumber: '4448',
					expirationDate: '12/26',
					isDefault: false,
					name: 'Visa',
				},
			]),
		);
		//TODO: uncomment when BE is done
		// return requestHelper<IUserPaymentMethod[]>(
		//   fetch(getState().fetchDefault.baseAPI + "api/User/PaymentMethods", {
		//     ...getState().fetchDefault.requestInit,
		//     method: "GET",
		//   })
		// )
		//   .then((res) => {
		//     dispatch(getPaymentMethodsSuccess(res));
		//   })
		//   .catch((err) => {
		//     console.error(err);
		//     dispatch(requestFailure(err));
		//   });
	};
}

/** Async Action Creator */
export function setDefaultPaymentMethod(paymentMethodId: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/User/PaymentMethods/' + paymentMethodId + '/SetDefault',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(updateDefaultPaymentMethodInList(paymentMethodId));
					dispatch(
						notifSend({
							message: 'Set efault payment method success',
							kind: 'success',
							dismissAfter: 3000,
						}),
					);
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Async Action Creator */
export function updateDefaultPaymentMethodInList(paymentMethodId: string) {
	return (dispatch, getState: () => IStore) => {
		let updatedPaymentMethods = [];
		const userPaymentMethods = getState().userPaymentMethods.items;
		if (userPaymentMethods && userPaymentMethods.length > 0) {
			updatedPaymentMethods = userPaymentMethods.map((paymentMethod: IUserPaymentMethod) => {
				paymentMethod.isDefault = paymentMethod.id === paymentMethodId;
				return paymentMethod;
			});
		}
		dispatch(getPaymentMethodsSuccess(updatedPaymentMethods));
	};
}

/** Async Action Creator */
export function deletePaymentMethod(id: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());
		return fetch(
			getState().fetchDefault.baseAPI + 'api/User/PaymentMethods/' + id,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'DELETE',
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(deletePaymentMethodFromList(id));
					dispatch(
						notifSend({
							message: 'Payment method deleted',
							kind: 'success',
							dismissAfter: 3000,
						}),
					);
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function deletePaymentMethodFromList(id: string) {
	return (dispatch, getState: () => IStore) => {
		const items: IUserPaymentMethod[] = getState().userPaymentMethods.items;
		const index = items.indexOf(items.find((x: IUserPaymentMethod) => x.id === id));
		items.splice(index, 1);
		dispatch(getPaymentMethodsSuccess(items));
	};
}

/** Action Creator - create */
export function addPaymentMethod(paymentMethod: IUserPaymentMethod) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		const userId: string = getState().userPreferences.id;
		paymentMethod.userId = userId;

		return fetch(
			getState().fetchDefault.baseAPI + 'api/User/PaymentMethods',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(paymentMethod),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((newPaymentMethod: IUserPaymentMethod) => {
						dispatch(addUserPaymentMethodSuccess(newPaymentMethod));
						dispatch(
							notifSend({
								message: i18n.t('Notification_Success_User_PaymentMethods'),
								kind: 'success',
								dismissAfter: 3000,
							}),
						);
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function requestStart(): IUserPaymentMethodsAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IUserPaymentMethodsAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function getPaymentMethodsSuccess(items: IUserPaymentMethod[]): IUserPaymentMethodsAction {
	return {
		type: GET_PAYMENT_METHODS_SUCCESS,
		payload: {
			items,
		},
	};
}

/** Action Creator */
export function addUserPaymentMethodSuccess(paymentMethod: IUserPaymentMethod): IUserPaymentMethodsAction {
	return {
		type: ADD_USER_PAYMENT_METHOD_SUCCESS,
		payload: {
			paymentMethod,
		},
	};
}
